/*
  Distribution Network 그래프의 데이터 → 기하(geometry) 변환. 순수 함수만 두고 JSX 는 없다.

  노드 순서는 계층 → 사이트 → 코드 하나로 고정한다. 좌측 목록과 그래프가 서로 다른 순서로
  늘어서면 목록에서 찾은 위치를 그래프에서 다시 눈으로 찾아야 하기 때문이다.

  ReactFlow 를 쓰지 않는 이유: 이 그래프는 사용자가 노드를 끌어 배치하는 다이어그램이 아니라
  "공급 단계가 위에서 아래로 흐른다"는 고정된 읽기 순서를 가진 그림이다. 자유 배치 엔진을 얹으면
  단계 정렬을 매번 되돌려야 하고(기존 화면이 그랬다), 좌표를 직접 잡으면 lane/밴드가 그대로 유지된다.
*/

import { darken, lighten } from "@mui/material/styles";

const NODE_W = 128;
const NODE_H = 42;
const NODE_GAP = 10;      // 같은 밴드 안 노드 간격
const BAND_GAP = 30;      // 밴드(사이트 묶음) 간격
const BAND_PAD = 9;       // 밴드 테두리와 노드 사이 여백
const LANE_GAP = 104;     // lane 사이 세로 여백(엣지가 지나가는 구간)
const LANE_TOP = 40;
const CANVAS_PAD_X = 28;
// 왼쪽 거터 — lane 이름(공장·물류센터 …)이 들어간다. 밴드 이름이 밴드 위에 붙으므로
// lane 이름을 같은 줄에 두면 겹친다. 폭을 미리 떼어 두고 그 안에만 쓴다.
// 유형 이름 하나("Distribution Center")가 한 줄에 들어가는 폭 — 여러 유형은 줄을 나눠 쓴다.
const LANE_LABEL_GUTTER = 136;
const GATE_LIFT = 34;     // 번들 게이트가 lane 상단에서 얼마나 위에 뜨는지

// 한 노드가 다음 lane 으로 이 개수 이상, 두 개 이상의 밴드에 걸쳐 뻗으면 "허브"로 보고 엣지를 묶는다.
// 허브 하나가 20갈래로 갈라지면 선이 부채처럼 겹쳐 어느 선이 어디로 가는지 못 읽는다 —
// 밴드 앞에 게이트를 하나 두고 거기까지 굵은 줄기 하나로 모은 뒤 짧게 갈라 준다.
const BUNDLE_MIN_TARGETS = 5;
const BUNDLE_MIN_BANDS = 2;

export const GRAPH_METRICS = { NODE_W, NODE_H, LANE_TOP, LANE_GAP, LANE_LABEL_GUTTER };

const bandKeyOf = loc => loc?.siteCd || "";

/**
 * 위치·BOD 목록을 그래프가 다룰 수 있는 형태로 정규화한다.
 * @param {Array} locations  /api/distribution-network 의 locationResults
 * @param {Array} bods       활성 BOD 목록
 * @param {Object} itemCounts  bodId → 활성 품목 수
 */
export const buildNetwork = ({ locations = [], bods = [], itemCounts = {} }) => {
  const byCd = new Map();
  for (const loc of locations) {
    if (!loc?.locCd) {
      continue;
    }
    byCd.set(loc.locCd, {
      locCd: loc.locCd,
      name: loc.name || loc.locCd,
      locType: loc.locType || "",
      siteCd: loc.site?.siteCd || loc.siteCd || "",
      siteName: loc.site?.name || "",
      bandKey: bandKeyOf({ siteCd: loc.site?.siteCd || loc.siteCd }),
      loc,
      inbound: [],
      outbound: [],
    });
  }

  const links = [];
  for (const bod of bods) {
    const from = bod?.originLocation?.locCd;
    const to = bod?.destLocation?.locCd;
    // ACL 로 한쪽 위치가 잘린 BOD 는 그릴 수 없다 — 그래프에서만 빠지고 그리드에는 그대로 남는다.
    if (!from || !to || !byCd.has(from) || !byCd.has(to)) {
      continue;
    }
    const link = {
      id: bod.id,
      key: `${from}>${to}`,
      from,
      to,
      items: itemCounts?.[bod.id] ?? 0,
      avgLtDays: bod.avgLtDays,
      bod,
    };
    links.push(link);
    byCd.get(from).outbound.push(link);
    byCd.get(to).inbound.push(link);
  }

  const tierByCd = computeTiers(byCd, links);
  for (const node of byCd.values()) {
    node.tier = tierByCd.get(node.locCd);
    node.itemsIn = node.inbound.reduce((sum, l) => sum + l.items, 0);
    node.itemsOut = node.outbound.reduce((sum, l) => sum + l.items, 0);
  }

  const linked = [...byCd.values()].filter(n => n.tier !== null);
  const maxTier = linked.reduce((m, n) => Math.max(m, n.tier), 0);
  const tiers = [];
  for (let t = 0; t <= maxTier && linked.length; t++) {
    tiers.push(sortNodes(linked.filter(n => n.tier === t)));
  }

  return {
    byCd,
    links,
    tiers,
    // BOD 가 하나도 안 걸린 위치. 그래프 아래쪽에 따로 모아 두면 "연결을 빠뜨린 위치"가 눈에 띈다.
    orphans: sortNodes([...byCd.values()].filter(n => n.tier === null)),
    locCount: byCd.size,
    linkCount: links.length,
    itemTotal: links.reduce((sum, l) => sum + l.items, 0),
    maxLinkItems: links.reduce((m, l) => Math.max(m, l.items), 0),
  };
};

// 밴드(사이트) → 코드 순. 이 순서가 세 뷰 전체의 기준이다.
const sortNodes = nodes => nodes.slice().sort((a, b) => {
  if (a.bandKey !== b.bandKey) {
    return a.bandKey < b.bandKey ? -1 : 1;
  }
  return a.locCd < b.locCd ? -1 : 1;
});

/*
  계층 = 공급 출발점에서의 "가장 긴 경로" 깊이. 위상 정렬(Kahn)로 확정한다.
  최단 경로가 아니라 최장 경로를 쓰는 이유: 공장 → 점포 직납선이 하나 있다고 해서 그 점포를
  DC 와 같은 줄에 올리면 안 되기 때문이다(엣지가 lane 을 건너뛰는 건 괜찮지만 거꾸로 가면 안 된다).

  데이터에 순환(A→B→A)이 있으면 그 노드들은 큐에서 끝내 빠지지 못한다. 이때는 확정된 선행자 중
  가장 깊은 값 + 1 로 한 번 밀어 배치하고, 그래도 남으면 마지막 lane 에 둔다 — 순환 엣지는
  layoutGraph 에서 점선(역방향)으로 그려지므로 화면에서 구분된다.
*/
const computeTiers = (byCd, links) => {
  const tier = new Map();
  const indeg = new Map();
  const adj = new Map();
  for (const cd of byCd.keys()) {
    indeg.set(cd, 0);
    adj.set(cd, []);
  }
  for (const link of links) {
    adj.get(link.from).push(link.to);
    indeg.set(link.to, indeg.get(link.to) + 1);
  }

  const queue = [];
  for (const [cd, node] of byCd) {
    // 링크가 하나도 없는 위치는 lane 에 올리지 않는다(tier = null).
    if (!node.inbound.length && !node.outbound.length) {
      tier.set(cd, null);
    } else if (indeg.get(cd) === 0) {
      tier.set(cd, 0);
      queue.push(cd);
    }
  }

  for (let head = 0; head < queue.length; head++) {
    const cd = queue[head];
    for (const next of adj.get(cd)) {
      tier.set(next, Math.max(tier.get(next) ?? 0, (tier.get(cd) ?? 0) + 1));
      indeg.set(next, indeg.get(next) - 1);
      if (indeg.get(next) === 0) {
        queue.push(next);
      }
    }
  }

  const stuck = [...byCd.keys()].filter(cd => !tier.has(cd));
  if (stuck.length) {
    const settled = [...tier.values()].filter(v => typeof v === "number");
    const fallback = settled.length ? Math.max(...settled) : 0;
    for (const cd of stuck) {
      const resolved = byCd.get(cd).inbound
        .map(l => tier.get(l.from))
        .filter(v => typeof v === "number");
      tier.set(cd, resolved.length ? Math.max(...resolved) + 1 : fallback + 1);
    }
  }

  return tier;
};

/* ------------------------------------------------------------------ Graph */

// lane 안에서 노드를 밴드(사이트)별로 묶어 가로로 늘어놓고, lane 전체를 캔버스 가운데 정렬한다.
const layoutLane = (nodes, y, canvasWidth) => {
  const bands = [];
  let current = null;
  for (const node of nodes) {
    if (!current || current.key !== node.bandKey) {
      current = { key: node.bandKey, items: [] };
      bands.push(current);
    }
    current.items.push(node);
  }

  const laneWidth = bands.reduce((sum, band) => (
    sum + band.items.length * NODE_W + (band.items.length - 1) * NODE_GAP
  ), 0) + BAND_GAP * (bands.length - 1);

  // 거터를 뺀 나머지 폭 안에서 가운데 정렬한다(거터를 침범하면 lane 이름을 덮는다).
  let x = LANE_LABEL_GUTTER + Math.max(0, (canvasWidth - LANE_LABEL_GUTTER - CANVAS_PAD_X - laneWidth) / 2);
  const placed = [];
  const bandRects = [];
  for (const band of bands) {
    const bandX = x;   // 루프 변수 x 는 아래에서 전진하므로 이 밴드의 시작점을 고정해 둔다
    const bandWidth = band.items.length * NODE_W + (band.items.length - 1) * NODE_GAP;
    bandRects.push({
      key: band.key,
      x: bandX - BAND_PAD,
      y: y - 22,
      w: bandWidth + BAND_PAD * 2,
      h: NODE_H + 32,
      cx: bandX + bandWidth / 2,
      locCds: band.items.map(node => node.locCd),
    });
    band.items.forEach((node, idx) => {
      const nx = bandX + idx * (NODE_W + NODE_GAP);
      placed.push({ node, x: nx, y, w: NODE_W, h: NODE_H, cx: nx + NODE_W / 2, bandKey: band.key });
    });
    x += bandWidth + BAND_GAP;
  }

  return { placed, bandRects, laneWidth };
};

const laneNeedsWidth = nodes => {
  let bands = 0;
  let prev = null;
  for (const node of nodes) {
    if (node.bandKey !== prev) {
      bands++;
      prev = node.bandKey;
    }
  }
  return nodes.length * NODE_W + (nodes.length - 1) * NODE_GAP + Math.max(0, bands - 1) * BAND_GAP;
};

/**
 * 계층 lane 레이아웃. 노드 좌표 · 밴드 사각형 · 엣지 path 를 모두 픽셀로 확정해 돌려준다.
 * 반환 좌표계는 곧 SVG viewBox 이고, 화면 확대/축소는 컨테이너 transform 이 담당한다.
 */
export const layoutGraph = network => {
  const { tiers, orphans, links, byCd, maxLinkItems } = network;

  const laneRows = tiers.map((nodes, tier) => ({ tier, nodes }));
  const orphanRow = orphans.length ? { tier: -1, nodes: orphans } : null;

  const laneSpan = nodes => laneNeedsWidth(nodes) + LANE_LABEL_GUTTER + CANVAS_PAD_X;
  const canvasWidth = Math.max(
    720,
    ...laneRows.map(row => laneSpan(row.nodes)),
    orphanRow ? laneSpan(orphanRow.nodes) : 0
  );

  const pos = new Map();
  const lanes = [];
  const bands = [];
  laneRows.forEach((row, idx) => {
    const y = LANE_TOP + idx * (NODE_H + LANE_GAP);
    const { placed, bandRects } = layoutLane(row.nodes, y, canvasWidth);
    placed.forEach(p => pos.set(p.node.locCd, { ...p, tier: row.tier, laneIndex: idx }));
    bandRects.forEach(rect => bands.push({ ...rect, laneIndex: idx }));
    lanes.push({ tier: row.tier, y, nodes: placed, gates: new Map(bandRects.map(r => [r.key, r.cx])) });
  });

  let height = LANE_TOP + laneRows.length * (NODE_H + LANE_GAP);
  let orphanLane = null;
  if (orphanRow) {
    const y = height + 26;
    const { placed, bandRects } = layoutLane(orphanRow.nodes, y, canvasWidth);
    placed.forEach(p => pos.set(p.node.locCd, { ...p, tier: null, laneIndex: -1 }));
    orphanLane = {
      y,
      nodes: placed,
      rect: {
        x: Math.min(...bandRects.map(r => r.x)) - 6,
        y: y - 26,
        w: Math.max(...bandRects.map(r => r.x + r.w)) - Math.min(...bandRects.map(r => r.x)) + 12,
        h: NODE_H + 38,
      },
    };
    height = y + NODE_H + 40;
  } else {
    // 마지막 lane 아래에는 엣지가 지나가지 않으므로 lane 간격 대신 여백만 남긴다.
    height = Math.max(160, height - (LANE_GAP - 24));
  }

  const edges = buildEdges({ links, pos, lanes, byCd, maxLinkItems });

  return { width: canvasWidth, height, lanes, bands, orphanLane, edges, pos };
};

// 엣지 굵기는 품목 수에 비례하되 0.9~5px 로 눌러 둔다 — 굵기는 "대략 얼마나 많이"만 말하면 된다.
const strokeWidthFor = (items, maxItems) => (
  maxItems > 0 ? 0.9 + 4.1 * Math.min(1, items / maxItems) : 1.2
);

const buildEdges = ({ links, pos, lanes, byCd, maxLinkItems }) => {
  // 허브 판정: (출발 노드, 도착 lane) 별로 목적지가 몇 개 밴드에 걸쳐 몇 개인지 센다.
  const fanout = new Map();
  for (const link of links) {
    const a = pos.get(link.from);
    const b = pos.get(link.to);
    if (!a || !b || b.laneIndex <= a.laneIndex) {
      continue;
    }
    const key = `${link.from}@${b.laneIndex}`;
    if (!fanout.has(key)) {
      fanout.set(key, { count: 0, bands: new Set() });
    }
    const entry = fanout.get(key);
    entry.count++;
    entry.bands.add(b.bandKey);
  }

  const edges = [];
  const trunks = new Map();
  for (const link of links) {
    const a = pos.get(link.from);
    const b = pos.get(link.to);
    if (!a || !b) {
      continue;
    }
    const width = strokeWidthFor(link.items, maxLinkItems);

    // 역방향/같은 lane 엣지(순환·수평 이관). 오른쪽으로 크게 우회시키고 점선으로 구분한다.
    if (b.laneIndex <= a.laneIndex) {
      const y0 = a.y + a.h / 2;
      const y1 = b.y + b.h / 2;
      const bulge = 70 + Math.abs(y1 - y0) * 0.25;
      const x0 = a.x + a.w;
      const x1 = b.x + b.w;
      edges.push({
        link,
        width,
        dashed: true,
        pts: [x0, y0, x0 + bulge, y0, x1 + bulge, y1, x1, y1],
        d: `M ${x0},${y0} C ${x0 + bulge},${y0} ${x1 + bulge},${y1} ${x1},${y1}`,
      });
      continue;
    }

    const entry = fanout.get(`${link.from}@${b.laneIndex}`);
    const bundled = entry && entry.count >= BUNDLE_MIN_TARGETS && entry.bands.size >= BUNDLE_MIN_BANDS;
    if (bundled) {
      const gateX = lanes[b.laneIndex].gates.get(b.bandKey) ?? b.cx;
      const gateY = b.y - GATE_LIFT;
      const trunkKey = `${link.from}@${b.laneIndex}@${b.bandKey}`;
      if (!trunks.has(trunkKey)) {
        trunks.set(trunkKey, { key: trunkKey, from: link.from, items: 0, links: [], a, gateX, gateY, bandKey: b.bandKey });
      }
      const trunk = trunks.get(trunkKey);
      trunk.items += link.items;
      trunk.links.push(link);
      // 게이트 → 목적지 짧은 가지
      edges.push({
        link,
        width,
        dashed: false,
        gated: true,
        pts: [gateX, gateY, gateX, gateY + 14, b.cx, b.y - 18, b.cx, b.y],
        d: `M ${gateX},${gateY} C ${gateX},${gateY + 14} ${b.cx},${b.y - 18} ${b.cx},${b.y}`,
      });
      continue;
    }

    const y0 = a.y + a.h;
    const y1 = b.y;
    const mid = (y1 - y0) / 2;
    edges.push({
      link,
      width,
      // lane 을 두 단계 이상 건너뛰는 직납선은 점선으로 — 아래 lane 들을 가로지르므로 구분이 필요하다.
      dashed: b.laneIndex - a.laneIndex > 1,
      pts: [a.cx, y0, a.cx, y0 + mid, b.cx, y1 - mid, b.cx, y1],
      d: `M ${a.cx},${y0} C ${a.cx},${y0 + mid} ${b.cx},${y1 - mid} ${b.cx},${y1}`,
    });
  }

  const trunkEdges = [...trunks.values()].map(trunk => {
    const y0 = trunk.a.y + trunk.a.h;
    const mid = (trunk.gateY - y0) / 2;
    return {
      trunk: true,
      // 줄기도 클릭 대상이라 안정적인 식별자가 필요하다(선택 강조·React key).
      key: trunk.key,
      from: trunk.from,
      bandKey: trunk.bandKey,
      items: trunk.items,
      links: trunk.links,
      width: Math.min(9, strokeWidthFor(trunk.items, maxLinkItems) * 1.8),
      d: `M ${trunk.a.cx},${y0} C ${trunk.a.cx},${y0 + mid} ${trunk.gateX},${trunk.gateY - mid} ${trunk.gateX},${trunk.gateY}`,
    };
  });

  // 줄기를 먼저 깔아야 가는 가지선이 그 위에 얹힌다.
  return [...trunkEdges, ...edges].filter(e => e.trunk || byCd.has(e.link.from));
};

/* -------------------------------------------------------------- Traversal */

/** 강조 범위. DEPTH_ALL 은 닿는 곳 전부 — 허브에서는 거의 전체가 켜지므로 기본값이 아니다. */
export const DEPTH_ALL = Number.MAX_SAFE_INTEGER;
export const DEFAULT_DEPTH = 2;

/**
 * BOD id 집합(Set, 또는 BOD id 를 키로 둔 Map)에 해당하는 링크 key.
 *
 * 품목의 경로를 그래프에 칠할 때 쓴다. 거르지 않고 칠하기만 하는 이유는, 거르면
 * "이 품목이 전체 네트워크의 어디를 지나는가"를 볼 수 없기 때문이다 — 남은 것만 보이면
 * 그 경로가 큰 그림에서 어디쯤인지 감각이 사라진다.
 */
export const linksForBods = (network, bodIds) => {
  const keys = new Set();
  if (!bodIds || !bodIds.size) {
    return keys;
  }
  for (const link of network.links) {
    if (bodIds.has(link.id)) {
      keys.add(link.key);
    }
  }
  return keys;
};

/** 링크 key 집합이 닿는 위치. 경로 위 노드만 또렷하게 남기는 데 쓴다. */
export const nodesOnLinks = (network, linkKeys) => {
  const near = new Set();
  if (!linkKeys?.size) {
    return near;
  }
  for (const link of network.links) {
    if (linkKeys.has(link.key)) {
      near.add(link.from);
      near.add(link.to);
    }
  }
  return near;
};

/**
 * 따라가는 품목의 조달 설정을 선에 달 라벨로 — 우선순위(P1, P2 …)와 배분 비율(60%).
 *
 * 우선순위·비율은 z_bod_item(품목 × 링크) 값이라 품목을 따라갈 때만 뜻이 있다.
 * 그리고 **공급처가 둘 이상인 도착지로 들어가는 링크에만** 단다 — 공급처가 하나면 언제나
 * "P1 · 100%" 라 정보가 없고, 23개 선에 전부 달면 정작 갈리는 곳이 묻힌다.
 *
 * sourcingById: BOD id → { originPriority, originAllocRatio } (비율은 0~1, 도착지별 합 1)
 * 반환: linkKey → { priority, ratio, text, primary }
 */
export const sourcingLabels = (network, linkKeys, sourcingById) => {
  const labels = new Map();
  if (!linkKeys?.size || !sourcingById?.size) {
    return labels;
  }

  const inboundByDest = new Map();
  for (const link of network.links) {
    if (!linkKeys.has(link.key) || !sourcingById.has(link.id)) {
      continue;
    }
    if (!inboundByDest.has(link.to)) {
      inboundByDest.set(link.to, []);
    }
    inboundByDest.get(link.to).push(link);
  }

  for (const inbound of inboundByDest.values()) {
    if (inbound.length < 2) {
      continue;
    }
    const topPriority = Math.min(...inbound.map(l => sourcingById.get(l.id).originPriority ?? Infinity));
    for (const link of inbound) {
      const src = sourcingById.get(link.id);
      const text = sourcingText(src);
      if (text) {
        const priority = src.originPriority ?? null;
        labels.set(link.key, { priority, ratio: src.originAllocRatio ?? null, text, primary: priority === topPriority });
      }
    }
  }
  return labels;
};

/** 3차 베지어 위의 한 점. 선 위에 라벨을 얹을 자리를 잡는 데 쓴다(pts = x0,y0,c1x,c1y,c2x,c2y,x1,y1). */
export const pointOnEdge = (pts, t) => {
  const [x0, y0, x1, y1, x2, y2, x3, y3] = pts;
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return { x: a * x0 + b * x1 + c * x2 + d * x3, y: a * y0 + b * y1 + c * y2 + d * y3 };
};

/**
 * 받는 쪽 끝에서 세로로 rise 만큼 위, 선 위의 점 — 조달 라벨 자리.
 *
 * 곡선 길이의 비율(t)로 잡으면 lane 을 여럿 건너는 직송선은 라벨이 그래프 한가운데,
 * 엉뚱한 거점 위에 떨어진다(구미공장 → BR Sao Paulo DC 의 라벨이 Paris Store 위에 얹혔다).
 * 라벨은 "받는 곳이 공급을 어떻게 나누나"라서 받는 곳 바로 위에 있어야 읽힌다.
 * 아래로 내려가는 선은 y 가 단조 증가하므로 이분 탐색으로 t 를 찾는다.
 * 짧은 선(게이트 가지)이나 옆으로 도는 역방향 선은 가운데에 둔다.
 */
export const labelPointNearEnd = (pts, rise) => {
  const y0 = pts[1];
  const y3 = pts[7];
  if (y3 - y0 < rise * 1.5) {
    return pointOnEdge(pts, 0.5);
  }
  const targetY = y3 - rise;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (pointOnEdge(pts, mid).y < targetY) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return pointOnEdge(pts, (lo + hi) / 2);
};

/**
 * 선택 노드까지 오는 상류 경로 한 줄. 갈래가 여럿이면 품목 수가 가장 많은 쪽(주 공급선)을 따라간다.
 * 순환 데이터에서 무한 루프에 빠지지 않도록 방문한 노드를 만나면 멈춘다.
 */
const BY_ITEM_COUNT = (a, b) => b.items - a.items;

/*
  경로 한 줄의 공통 옵션 — 따라가는 품목이 있으면 그 품목의 링크로만 걷고(allowedKeys),
  갈래가 여럿이면 품목 수 대신 그 품목의 조달 우선순위로 고른다(compare).
  품목 수로 고르면 "품목 종류가 많은 선"을 따라가 그 품목이 실제로 오는 길과 어긋난다
  (mj: MT_ENT_01 은 화성공장 → 중부DC 로만 오는데, 품목 수 기준이라 구미공장이 나왔다).
*/
const pickLink = (links, { allowedKeys = null, compare = BY_ITEM_COUNT } = {}) => (
  (links ?? []).filter(link => !allowedKeys || allowedKeys.has(link.key)).sort(compare)[0]
);

export const upstreamChain = (network, locCd, options) => {
  const chain = [];
  const seen = new Set();
  let cursor = locCd;
  while (cursor && !seen.has(cursor) && chain.length < 8) {
    seen.add(cursor);
    chain.unshift(cursor);
    const best = pickLink(network.byCd.get(cursor)?.inbound, options);
    cursor = best ? best.from : null;
  }
  return chain;
};

/**
 * 선택 노드에서 아래로 내려가는 경로 한 줄. upstreamChain 의 거울상이다.
 * "어디서 받나" 만 알던 것을 "내가 보낸 게 어디까지 가나" 까지 잇는다.
 */
export const downstreamChain = (network, locCd, options) => {
  const chain = [];
  const seen = new Set();
  let cursor = locCd;
  while (cursor && !seen.has(cursor) && chain.length < 8) {
    seen.add(cursor);
    chain.push(cursor);
    const best = pickLink(network.byCd.get(cursor)?.outbound, options);
    cursor = best ? best.to : null;
  }
  return chain;
};

/**
 * 따라가는 품목의 조달 설정으로 링크를 비교한다 — 우선순위가 앞선 쪽, 같으면 비율이 큰 쪽.
 * sourcingById: BOD id → { originPriority, originAllocRatio }
 */
export const bySourcing = sourcingById => (a, b) => {
  const sa = sourcingById?.get(a.id) ?? {};
  const sb = sourcingById?.get(b.id) ?? {};
  return (sa.originPriority ?? Infinity) - (sb.originPriority ?? Infinity)
    || (Number(sb.originAllocRatio) || 0) - (Number(sa.originAllocRatio) || 0)
    || BY_ITEM_COUNT(a, b);
};

/** 링크 하나의 조달 설정을 "P1 · 60%" 로. 값이 없으면 빈 문자열. */
export const sourcingText = src => {
  if (!src) {
    return "";
  }
  const parts = [];
  if (src.originPriority != null) {
    parts.push(`P${src.originPriority}`);
  }
  if (src.originAllocRatio != null) {
    parts.push(`${Math.round(Number(src.originAllocRatio) * 100)}%`);
  }
  return parts.join(" · ");
};

export const REACH_DIR = { SELF: "self", UP: "up", DOWN: "down" };
export const REACH_ARROW = { [REACH_DIR.UP]: "↑", [REACH_DIR.DOWN]: "↓" };

/*
  상류(받아 오는 쪽)·하류(보내는 쪽)를 같은 색의 짙고 옅은 두 톤으로 가른다.
  색을 둘로 쪼개면 "강조색 = 선택 / 품목색 = 따라가기" 라는 뜻이 흐려진다 — 뜻은 색상(hue)이,
  방향은 명도가 맡는다. 그래프·인스펙터·범례가 같은 값을 써야 해서 여기 둔다.
*/
export const reachTones = base => ({
  [REACH_DIR.UP]: darken(base, 0.32),
  [REACH_DIR.DOWN]: lighten(base, 0.18),
});

/**
 * 선택 노드를 **지나는** 경로만 — 상류(받아 오는 쪽)와 하류(보내는 쪽)를 따로 걷는다.
 *
 * 예전(reachableWithin + linksWithin)은 방향을 가리지 않아서, 공급처로 올라갔다가 형제 거점으로
 * 도로 내려왔다 — JP East DC 를 고르면 구미공장 → DE Frankfurt DC 같은 옆길이 2홉으로 켜졌는데,
 * 그 선은 고른 거점으로 들어오지도 나가지도 않는다. 올라가는 길과 내려가는 길을 섞지 않으면
 * 옆길은 구조적으로 생기지 않고, 켜진 선마다 "받아 오는 쪽 / 보내는 쪽"이 정해진다.
 *
 * depth 를 크게 주면 허브에서 네트워크 대부분이 켜져 강조가 의미를 잃는다 — 화면 기본값은 2,
 * 무제한(DEPTH_ALL)은 사용자가 고를 때만이다.
 *
 * allowedKeys 를 주면 그 링크로만 걷는다 — 따라가는 품목이 실제로 타는 경로만 센다.
 *
 * 반환: nodes = locCd → { hop, dir }, links = linkKey → dir.
 *   순환(A→B→A)에서 같은 거점이 양쪽에 걸리면 상류가 먼저 잡는다.
 */
export const directedReach = (network, locCd, depth = 1, allowedKeys = null) => {
  const nodes = new Map();
  const links = new Map();
  if (!locCd || !network.byCd.has(locCd)) {
    return { nodes, links };
  }
  nodes.set(locCd, { hop: 0, dir: REACH_DIR.SELF });

  const walk = dir => {
    const up = dir === REACH_DIR.UP;
    const seen = new Set([locCd]);
    let frontier = [locCd];
    for (let d = 1; d <= depth && frontier.length; d++) {
      const next = [];
      for (const cd of frontier) {
        const node = network.byCd.get(cd);
        for (const link of (up ? node?.inbound : node?.outbound) ?? []) {
          if (allowedKeys && !allowedKeys.has(link.key)) {
            continue;
          }
          if (!links.has(link.key)) {
            links.set(link.key, dir);
          }
          const other = up ? link.from : link.to;
          if (seen.has(other)) {
            continue;
          }
          seen.add(other);
          next.push(other);
          if (!nodes.has(other)) {
            nodes.set(other, { hop: d, dir });
          }
        }
      }
      frontier = next;
    }
  };
  walk(REACH_DIR.UP);
  walk(REACH_DIR.DOWN);

  return { nodes, links };
};
